import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { printDoc, pmoney } from "@/lib/print";
import { usePrintFormat } from "@/components/print/PrintFormatDialog";
import { isThermalFormat } from "@/lib/printFormat";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ManagerPinModal from "@/components/ManagerPinModal";
import { ClipboardList, Printer, Plus, Minus, Lock, DollarSign } from "lucide-react";

const SHORT_REASONS = ["Miscount", "Theft / Missing Cash", "Register Error", "Unrecorded Payout", "Other"];

export default function ShiftReport() {
  const { business } = useAuth();
  const cur = business?.currency || "JMD $";
  const [current, setCurrent] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [opening, setOpening] = useState("10000");
  const [closeOpen, setCloseOpen] = useState(false);
  const [actualCash, setActualCash] = useState("");
  const [cashOpen, setCashOpen] = useState(false);
  const shiftInFlight = useRef(false); const [shiftBusy, setShiftBusy] = useState(false);
  const guarded = async (fn) => { if (shiftInFlight.current) return; shiftInFlight.current = true; setShiftBusy(true); try { await fn(); } finally { shiftInFlight.current = false; setShiftBusy(false); } };
  const [mv, setMv] = useState({ type: "add", amount: "", reason: "" });
  const [cashPinOpen, setCashPinOpen] = useState(false);
  const [closePinOpen, setClosePinOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closeReasonOther, setCloseReasonOther] = useState("");
  // Presentation selector (Full Page default / Receipt 80mm·58mm) — same engine the sales receipts use.
  const { requestPrint: requestShiftPrint, dialog: shiftPrintDialog } = usePrintFormat("shift_report", "Print Shift Report");

  const load = () => {
    api.get("/shifts/current").then((r) => setCurrent(r.data));
    api.get("/shifts").then((r) => setShifts(r.data));
  };
  useEffect(load, []);

  const openShift = async () => {
    try { await api.post("/shifts/open", { opening_cash: parseFloat(opening || 0) }); toast.success("Shift opened"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addCash = (pin = null) => guarded(() => _addCash(pin));
  const _addCash = async (pin = null) => {
    try {
      await api.post(`/shifts/${current.id}/cash`, { type: mv.type, amount: parseFloat(mv.amount || 0), reason: mv.reason, manager_pin: pin });
      setCashOpen(false); setCashPinOpen(false); setMv({ type: "add", amount: "", reason: "" }); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.needs_approval) { if (pin) throw new Error(d.message || "Invalid PIN"); setCashPinOpen(true); }
      else { setCashPinOpen(false); toast.error(apiError(d)); if (pin) throw new Error("Failed"); }
    }
  };

  const shortReasonStr = () => (closeReason === "Other" ? closeReasonOther.trim() : closeReason);

  const closeShift = (pin = null) => guarded(() => _closeShift(pin));
  const _closeShift = async (pin = null) => {
    const overShort = parseFloat(actualCash || 0) - (current?.expected_cash || 0);
    const tolerance = business?.shift_short_tolerance ?? 500;
    const needsReason = overShort < 0 && Math.abs(overShort) > tolerance;
    if (needsReason && !shortReasonStr()) { toast.error("Select a reason for the shortage"); return; }
    try {
      const { data } = await api.post(`/shifts/${current.id}/close`, {
        actual_cash: parseFloat(actualCash || 0),
        reason: needsReason ? shortReasonStr() : "",
        manager_pin: pin,
      });
      setCloseOpen(false); setClosePinOpen(false); setActualCash(""); setCloseReason(""); setCloseReasonOther("");
      load(); toast.success("Shift closed");
      requestShiftPrint((format) => printShift(data, format));   // shift is already closed server-side; printing is presentation only
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.needs_approval) { if (pin) throw new Error(d.message || "Invalid PIN"); setClosePinOpen(true); }
      else if (d?.reason_required) { toast.error(d.message || "A reason is required to close a short shift"); }
      else { setClosePinOpen(false); toast.error(apiError(d)); if (pin) throw new Error("Failed"); }
    }
  };

  // One authoritative closed-shift payload `s` (from the API) → two presentations. No recalculation here.
  const printShift = (s, format = "standard") => {
    const ap = s.account_payments || {};
    const nz = (n) => Math.abs(Number(n || 0)) >= 0.005;
    const rows = [
      ["Opening Cash", s.opening_cash],
      ["Cash Sales", s.cash_sales],
      ["WiPay / Card Sales", s.card_sales],
      ["Bank Transfer Sales", s.bank_sales],
      ["Credit Sales (on account)", s.credit_sales],
      ["Account Payments: Cash", ap.cash],
      ["Account Payments: Card", ap.card],
      ["Account Payments: Bank", ap.bank],
      ["Cash Additions", s.cash_additions],
      ["Cash Removals", s.cash_removals],
      ["Refunds / Voids", s.refunds],
    ].filter((r) => nz(r[1])).map((r) => [r[0], pmoney(r[1], cur)]);
    const totals = [
      ["Expected Cash", pmoney(s.expected_cash, cur)],
      ["Actual Counted Cash", s.actual_cash == null ? "-" : pmoney(s.actual_cash, cur)],
      ["Over / Short", s.over_short == null ? "-" : pmoney(s.over_short, cur)],
    ];
    const meta = [["Cashier", s.cashier_name || "-"], ["Register", s.register || "-"], ["Opened", fmtDate(s.opened_at)], ["Closed", s.closed_at ? fmtDate(s.closed_at) : "Open"]];
    const rowsHtml = rows.map((r) => `<tr><td>${r[0]}</td><td class="right">${r[1]}</td></tr>`).join("");
    const fullBody = `${rows.length ? `<table class="data" data-doc="shift-rows"><tbody>${rowsHtml}</tbody></table>` : ""}
      <table class="totals" data-doc="shift-totals">
        <tr><td>${totals[0][0]}</td><td class="right">${totals[0][1]}</td></tr>
        <tr><td>${totals[1][0]}</td><td class="right">${totals[1][1]}</td></tr>
        <tr class="grand"><td>${totals[2][0]}</td><td class="right">${totals[2][1]}</td></tr>
      </table>`;
    // Thermal: stacked label/value rows from the receipt template (no 4-column item grid).
    const thermalBody = `<style>.row .lbl{white-space:normal!important}.row .val{white-space:nowrap}</style>
      <div data-doc="shift-rows">${rows.map((r) => `<div class="row"><span class="lbl">${r[0]}</span><span class="val">${r[1]}</span></div>`).join("")}</div>
      <table class="totals" data-doc="shift-totals">
        <tr><td>${totals[0][0]}</td><td class="right num">${totals[0][1]}</td></tr>
        <tr class="cash"><td>${totals[1][0]}</td><td class="right num">${totals[1][1]}</td></tr>
        <tr class="grand"><td>${totals[2][0]}</td><td class="right num">${totals[2][1]}</td></tr>
      </table>`;
    printDoc({ business, docTitle: "Cashier Shift Report", meta, format, plain: true,
      body: isThermalFormat(format) ? thermalBody : fullBody });
  };

  return (
    <div className="space-y-6">
      {shiftPrintDialog}
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><ClipboardList className="w-7 h-7 text-emerald-500" /> Cashier Shift Report</h1>
        <p className="text-muted-foreground">Open a drawer, track takings by payment type, then close and reconcile over/short.</p>
      </div>

      {!current ? (
        <Card className="p-6 max-w-md">
          <p className="font-head font-bold mb-1">Open a shift</p>
          <p className="text-sm text-muted-foreground mb-4">Enter the opening cash (float) in the drawer.</p>
          <Label className="text-xs">Opening Cash</Label>
          <Input type="number" value={opening} onChange={(e) => setOpening(e.target.value)} className="mono text-lg h-12" data-testid="opening-cash" />
          <Button onClick={openShift} className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="open-shift-btn"><Lock className="w-4 h-4 mr-2" /> Open Shift</Button>
        </Card>
      ) : (
        <Card className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="font-head font-bold flex items-center gap-2">Open Shift · {current.cashier_name} <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">LIVE</Badge></div>              <p className="text-xs text-muted-foreground">Opened {fmtDate(current.opened_at)} · {current.register}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCashOpen(true)} data-testid="cash-movement-btn"><DollarSign className="w-4 h-4 mr-1.5" /> Cash In/Out</Button>
              <Button onClick={() => setCloseOpen(true)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="close-shift-btn"><Lock className="w-4 h-4 mr-1.5" /> Close Shift</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Tile label="Opening Cash" value={money(current.opening_cash)} />
            <Tile label="Cash Sales" value={money(current.cash_sales)} />
            <Tile label="WiPay / Card" value={money(current.card_sales)} />
            <Tile label="Bank Transfer" value={money(current.bank_sales)} />
            <Tile label="Credit Sales" value={money(current.credit_sales)} />
            <Tile label="Acct Pmts (Cash)" value={money(current.account_payments?.cash)} />
            <Tile label="Cash In / Out" value={`${money(current.cash_additions)} / ${money(current.cash_removals)}`} />
            <Tile label="Expected Cash" value={money(current.expected_cash)} accent="text-emerald-500" />
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <p className="font-head font-bold p-4 border-b border-border">Shift History</p>
        <Table>
          <TableHeader><TableRow><TableHead>Cashier</TableHead><TableHead>Opened</TableHead><TableHead>Closed</TableHead><TableHead className="text-right">Expected</TableHead><TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Over/Short</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {shifts.map((s) => (
              <TableRow key={s.id} data-testid={`shift-row-${s.id}`}>
                <TableCell className="font-medium">{s.cashier_name}</TableCell>
                <TableCell className="text-xs">{fmtDate(s.opened_at)}</TableCell>
                <TableCell className="text-xs">{s.closed_at ? fmtDate(s.closed_at) : <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Open</Badge>}</TableCell>
                <TableCell className="text-right mono">{money(s.expected_cash)}</TableCell>
                <TableCell className="text-right mono">{s.actual_cash == null ? "-" : money(s.actual_cash)}</TableCell>
                <TableCell className="text-right mono font-bold">{s.over_short == null ? "-" : <span className={s.over_short < 0 ? "text-rose-500" : s.over_short > 0 ? "text-amber-500" : "text-emerald-500"}>{money(s.over_short)}</span>}</TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={() => requestShiftPrint((format) => printShift(s, format))} data-testid={`print-shift-${s.id}`}><Printer className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {shifts.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No shifts yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={cashOpen} onOpenChange={setCashOpen}>
        <DialogContent className="max-w-sm" data-testid="cash-modal">
          <DialogHeader><DialogTitle className="font-head">Cash In / Out</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Button variant={mv.type === "add" ? "default" : "outline"} className="flex-1" onClick={() => setMv({ ...mv, type: "add" })} data-testid="cash-add"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            <Button variant={mv.type === "remove" ? "default" : "outline"} className="flex-1" onClick={() => setMv({ ...mv, type: "remove" })} data-testid="cash-remove"><Minus className="w-4 h-4 mr-1" /> Remove</Button>
          </div>
          <Label className="text-xs">Amount</Label>
          <Input type="number" value={mv.amount} onChange={(e) => setMv({ ...mv, amount: e.target.value })} className="mono" data-testid="cash-amount" />
          <Label className="text-xs">Reason</Label>
          <Input value={mv.reason} onChange={(e) => setMv({ ...mv, reason: e.target.value })} data-testid="cash-reason" />
          {mv.type === "remove" && (
            <p className="text-xs text-muted-foreground">Large cash removals may require Manager/Owner approval.</p>
          )}
          <Button onClick={() => addCash()} disabled={shiftBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cash-save">{shiftBusy ? "Processing…" : "Record"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-sm" data-testid="close-modal">
          <DialogHeader><DialogTitle className="font-head">Close Shift</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Expected cash in drawer: <span className="mono font-bold text-foreground">{money(current?.expected_cash)}</span></p>
          <Label className="text-xs">Actual Counted Cash</Label>
          <Input type="number" value={actualCash} onChange={(e) => setActualCash(e.target.value)} className="mono text-lg h-12" data-testid="actual-cash" placeholder="0.00" />
          {(() => {
            if (actualCash === "" || !current) return null;
            const os = parseFloat(actualCash || 0) - current.expected_cash;
            const tolerance = business?.shift_short_tolerance ?? 500;
            const needsReason = os < 0 && Math.abs(os) > tolerance;
            return (
              <>
                <p className="text-sm">Over / Short: <span className={`mono font-bold ${os < 0 ? "text-rose-500" : "text-emerald-500"}`}>{money(os)}</span></p>
                {needsReason && (
                  <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 space-y-2" data-testid="short-reason-block">
                    <p className="text-xs font-semibold text-rose-500 uppercase tracking-wider">Shortage over tolerance ({money(tolerance)})</p>
                    <Label className="text-xs">Reason for shortage (required)</Label>
                    <select value={closeReason} onChange={(e) => setCloseReason(e.target.value)}
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="short-reason-select">
                      <option value="">Select a reason…</option>
                      {SHORT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    {closeReason === "Other" && (
                      <Input value={closeReasonOther} onChange={(e) => setCloseReasonOther(e.target.value)}
                        placeholder="Describe the reason" data-testid="short-reason-other" />
                    )}
                    <p className="text-xs text-muted-foreground">A Manager or Owner PIN may be required to close a short shift.</p>
                  </div>
                )}
              </>
            );
          })()}
          <Button onClick={() => closeShift()} disabled={shiftBusy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-close">{shiftBusy ? "Processing…" : <>Close &amp; Print Report</>}</Button>
        </DialogContent>
      </Dialog>

      <ManagerPinModal open={cashPinOpen} onClose={() => setCashPinOpen(false)}
        title="Cash Removal Approval"
        reason="This cash removal exceeds the cashier limit. A Manager or Owner PIN is required."
        onSubmit={(pin) => addCash(pin)} />

      <ManagerPinModal open={closePinOpen} onClose={() => setClosePinOpen(false)}
        title="Short Shift Approval"
        reason="This shift is short beyond the tolerance. A Manager or Owner PIN is required to close it."
        onSubmit={(pin) => closeShift(pin)} />
    </div>
  );
}

const Tile = ({ label, value, accent }) => (
  <div className="rounded-xl border border-border p-3"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p><p className={`mono font-bold ${accent || ""}`}>{value}</p></div>
);
